// Health Data Service for fetching real data from external APIs
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface HealthMetric {
  name: string;
  value: string | number;
  status: 'healthy' | 'warning' | 'error' | 'info';
  description: string;
  links?: Array<{ url: string; title: string }>;
  reportUrls?: Array<{ url: string; title: string }>; // All report URLs for iframe display
}

export interface GitLabPipelineStatus {
  status: string;
  web_url: string;
  created_at: string;
}

export interface SonarQubeMetrics {
  quality_gate_status: string;
  coverage: number;
  duplications: number;
  vulnerabilities: number;
}

export class HealthDataService {
  private gitlabToken: string;
  private discoveryApi?: DiscoveryApi;
  private fetchApi?: FetchApi;

  constructor(
    gitlabToken: string,
    _sonarToken?: string, // Deprecated: token now handled on backend
    discoveryApi?: DiscoveryApi,
    fetchApi?: FetchApi
  ) {
    this.gitlabToken = gitlabToken;
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  async getGitLabPipelineStatus(projectId: string): Promise<HealthMetric> {
    try {
      console.log('Fetching GitLab pipeline status for project:', projectId);
      console.log('Using GitLab token:', this.gitlabToken ? 'Present' : 'Missing');
      
      if (!this.gitlabToken) {
        return {
          name: 'Pipeline Status',
          value: 'No GitLab Token',
          status: 'error',
          description: '❌ GitLab API token missing. Add REACT_APP_GITLAB_TOKEN to .env file to fetch real pipeline data from your GitLab project.',
        };
      }

      const response = await fetch(
        `https://git.agilie.com/api/v4/projects/${projectId}/pipelines?per_page=1`,
        {
          headers: {
            'PRIVATE-TOKEN': this.gitlabToken,
          },
        }
      );

      console.log('GitLab API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('GitLab API error:', response.status, errorText);
        return {
          name: 'Pipeline Status',
          value: `GitLab API Error ${response.status}`,
          status: 'error',
          description: `❌ Cannot connect to GitLab API (${response.status}). Check if project ID ${projectId} exists and token has access.`,
        };
      }

      const pipelines = await response.json();
      console.log('GitLab pipelines response:', pipelines);
      
      if (pipelines.length === 0) {
        return {
          name: 'Pipeline Status',
          value: 'No Pipelines Found',
          status: 'info',
          description: 'ℹ️ No CI/CD pipelines found for this GitLab project. Create a .gitlab-ci.yml file to enable pipeline monitoring.',
        };
      }

      const latestPipeline = pipelines[0];
      const status = latestPipeline.status;
      
      return {
        name: 'Pipeline Status',
        value: status === 'success' ? '✅ Passing' : status === 'failed' ? '❌ Failed' : status,
        status: status === 'success' ? 'healthy' : status === 'failed' ? 'error' : 'warning',
        description: `🔄 Real data from GitLab: Latest pipeline ${status} (${new Date(latestPipeline.created_at).toLocaleDateString()})`,
      };
    } catch (error) {
      console.error('Error fetching GitLab pipeline status:', error);
      return {
        name: 'Pipeline Status',
        value: 'Connection Failed',
        status: 'error',
        description: `❌ Cannot connect to GitLab API: ${error.message}. Check network connection and GitLab server status.`,
      };
    }
  }

  async getSonarQubeMetrics(projectKey: string, instance?: string): Promise<HealthMetric> {
    
    if (!this.discoveryApi || !this.fetchApi) {
      return {
        name: 'SonarQube',
        value: 'Configuration Error',
        status: 'error',
        description: '⚠️ Backstage API not configured. DiscoveryApi and FetchApi are required.',
      };
    }

    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('backend');
      const backendBase = baseUrl.replace(/\/api\/backend$/, '');
      
      const params = new URLSearchParams();
      params.append('componentKey', projectKey);
      
      if (instance && !instance.includes('.') && !instance.includes('/')) {
        params.append('instanceKey', instance);
      }
      
      const url = `${backendBase}/api/sonarqube/findings?${params.toString()}`;
      
      const response = await this.fetchApi.fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('SonarQube API error:', response.status, errorText);
        throw new Error(`SonarQube API error: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      
      if (!responseText || responseText.trim() === '' || responseText.trim() === 'null') {
        return {
          name: 'SonarQube',
          value: 'No Data',
          status: 'warning',
          description: `No SonarQube data for "${projectKey}". Project might not exist or has not been analyzed.`,
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse SonarQube response:', responseText);
        throw new Error(`Failed to parse SonarQube response: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }

      if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
        return {
          name: 'SonarQube',
          value: 'No Data',
          status: 'warning',
          description: `No SonarQube findings available for project "${projectKey}". Project might not exist or has not been analyzed yet.`,
        };
      }
      
      const measures = data.measures || [];
      const analysisDate = data.analysisDate;
      
      const alertStatus = measures.find((m: { metric: string; value: string }) => m.metric === 'alert_status')?.value;
      const coverage = measures.find((m: { metric: string; value: string }) => m.metric === 'coverage')?.value;
      const duplications = measures.find((m: { metric: string; value: string }) => m.metric === 'duplicated_lines_density')?.value;
      const bugs = measures.find((m: { metric: string; value: string }) => m.metric === 'bugs')?.value;
      const vulnerabilities = measures.find((m: { metric: string; value: string }) => m.metric === 'vulnerabilities')?.value;
      const codeSmells = measures.find((m: { metric: string; value: string }) => m.metric === 'code_smells')?.value;
      const reliabilityRating = measures.find((m: { metric: string; value: string }) => m.metric === 'reliability_rating')?.value;
      const securityRating = measures.find((m: { metric: string; value: string }) => m.metric === 'security_rating')?.value;

      const status = alertStatus === 'OK' || alertStatus === 'PASSED' ? 'healthy' : 
                     alertStatus === 'WARN' || alertStatus === 'WARNING' ? 'warning' : 'error';
      const value = alertStatus === 'OK' || alertStatus === 'PASSED' ? 'Passed' : 
                   alertStatus === 'WARN' || alertStatus === 'WARNING' ? 'Warning' : 
                   alertStatus === 'ERROR' || alertStatus === 'FAILED' ? 'Failed' : 'N/A';

      const descriptionParts = [];
      if (alertStatus) descriptionParts.push(`Status: ${alertStatus}`);
      if (coverage !== undefined) descriptionParts.push(`Coverage: ${coverage}%`);
      if (duplications !== undefined) descriptionParts.push(`Duplications: ${duplications}%`);
      if (bugs !== undefined) descriptionParts.push(`Bugs: ${bugs}`);
      if (vulnerabilities !== undefined) descriptionParts.push(`Vulnerabilities: ${vulnerabilities}`);
      if (codeSmells !== undefined) descriptionParts.push(`Code Smells: ${codeSmells}`);
      if (reliabilityRating !== undefined) descriptionParts.push(`Reliability: ${reliabilityRating}`);
      if (securityRating !== undefined) descriptionParts.push(`Security: ${securityRating}`);
      if (analysisDate) {
        const date = new Date(analysisDate);
        descriptionParts.push(`Last analysis: ${date.toLocaleDateString()}`);
      }

      return {
        name: 'SonarQube',
        value: value,
        status: status,
        description: descriptionParts.join(', ') || 'No metrics available',
      };
    } catch (error) {
      console.error('Error fetching SonarQube metrics:', error);
      return {
        name: 'SonarQube',
        value: 'Error',
        status: 'error',
        description: `Failed to fetch SonarQube data: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  async getGitLabProjectInfo(projectId: string): Promise<{ lastActivity: string; openIssues: number }> {
    try {
      const response = await fetch(
        `https://git.agilie.com/api/v4/projects/${projectId}`,
        {
          headers: {
            'PRIVATE-TOKEN': this.gitlabToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`GitLab API error: ${response.status}`);
      }

      const project = await response.json();
      
      // Get open issues count
      const issuesResponse = await fetch(
        `https://git.agilie.com/api/v4/projects/${projectId}/issues?state=opened&per_page=1`,
        {
          headers: {
            'PRIVATE-TOKEN': this.gitlabToken,
          },
        }
      );

      const issuesData = await issuesResponse.json();
      const totalIssues = issuesResponse.headers.get('X-Total') || '0';

      return {
        lastActivity: project.last_activity_at,
        openIssues: parseInt(totalIssues),
      };
    } catch (error) {
      console.error('Error fetching GitLab project info:', error);
      return {
        lastActivity: 'Unknown',
        openIssues: 0,
      };
    }
  }

  async getTestCoverage(projectId: string): Promise<HealthMetric> {
    try {
      console.log('Fetching test coverage for project:', projectId);
      
      if (!this.gitlabToken) {
        return {
          name: 'Test Coverage',
          value: 'No GitLab Token',
          status: 'error',
          description: '❌ GitLab API token missing. Add REACT_APP_GITLAB_TOKEN to .env file to fetch real test coverage data.',
        };
      }

      // Try to get coverage from GitLab CI/CD artifacts
      const response = await fetch(
        `https://git.agilie.com/api/v4/projects/${projectId}/jobs?per_page=10&scope[]=success`,
        {
          headers: {
            'PRIVATE-TOKEN': this.gitlabToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`GitLab API error: ${response.status}`);
      }

      const jobs = await response.json();

      // Look for test jobs with coverage
      const testJob = jobs.find((job: any) => 
        job.name?.toLowerCase().includes('test') || 
        job.name?.toLowerCase().includes('coverage') ||
        job.name?.toLowerCase().includes('nyc')
      );

      if (testJob) {
        // Try to get coverage from job artifacts
        const artifactsResponse = await fetch(
          `https://git.agilie.com/api/v4/projects/${projectId}/jobs/${testJob.id}/artifacts/coverage/lcov.info`,
          {
            headers: {
              'PRIVATE-TOKEN': this.gitlabToken,
            },
          }
        );

        if (artifactsResponse.ok) {
          const coverageData = await artifactsResponse.text();
          // Parse LCOV data to extract coverage percentage
          const lines = coverageData.split('\n');
          const lfLines = lines.filter(line => line.startsWith('LF:'));
          const lhLines = lines.filter(line => line.startsWith('LH:'));
          
          if (lfLines.length > 0 && lhLines.length > 0) {
            const totalLines = parseInt(lfLines[0].split(':')[1]);
            const hitLines = parseInt(lhLines[0].split(':')[1]);
            const coverage = Math.round((hitLines / totalLines) * 100);
            
            return {
              name: 'Test Coverage',
              value: `${coverage}%`,
              status: coverage >= 80 ? 'healthy' : coverage >= 60 ? 'warning' : 'error',
              description: `🔄 Real data from GitLab CI: ${coverage}% coverage from latest test run (${testJob.created_at})`,
            };
          }
        }
      }
      console.log(jobs)
      // Fallback: Check if there are any recent test jobs
      if (jobs.length > 0) {
        const latestJob = jobs[0];
        return {
          name: 'Test Coverage',
          value: 'Tests Running',
          status: 'info',
          description: `ℹ️ Test jobs found but coverage data not available. Latest job: ${latestJob.name} (${latestJob.status})`,
        };
      }

      return {
        name: 'Test Coverage',
        value: 'No Tests Found',
        status: 'warning',
        description: '⚠️ No test jobs found in GitLab CI. Configure test coverage reporting in your CI/CD pipeline.',
      };

    } catch (error) {
      console.error('Error fetching test coverage:', error);
      return {
        name: 'Test Coverage',
        value: 'Error',
        status: 'error',
        description: `❌ Failed to fetch test coverage: ${error.message}`,
      };
    }
  }

  async getBuildStatus(projectId: string): Promise<HealthMetric> {
    try {
      console.log('Fetching build status for project:', projectId);
      
      if (!this.gitlabToken) {
        return {
          name: 'Build Status',
          value: 'No GitLab Token',
          status: 'error',
          description: '❌ GitLab API token missing. Add REACT_APP_GITLAB_TOKEN to .env file to fetch real build status.',
        };
      }

      const response = await fetch(
        `https://git.agilie.com/api/v4/projects/${projectId}/pipelines?per_page=5`,
        {
          headers: {
            'PRIVATE-TOKEN': this.gitlabToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`GitLab API error: ${response.status}`);
      }

      const pipelines = await response.json();
      console.log('GitLab pipelines response:', pipelines);

      if (pipelines.length === 0) {
        return {
          name: 'Build Status',
          value: 'No Builds',
          status: 'info',
          description: 'ℹ️ No build pipelines found. Configure CI/CD in your GitLab project.',
        };
      }

      const latestPipeline = pipelines[0];
      const status = latestPipeline.status;
      const duration = latestPipeline.duration;
      const createdAt = new Date(latestPipeline.created_at);

      let statusText = '';
      let statusColor = 'info';

      switch (status) {
        case 'success':
          statusText = '✅ Success';
          statusColor = 'healthy';
          break;
        case 'failed':
          statusText = '❌ Failed';
          statusColor = 'error';
          break;
        case 'running':
          statusText = '🔄 Running';
          statusColor = 'warning';
          break;
        case 'pending':
          statusText = '⏳ Pending';
          statusColor = 'warning';
          break;
        default:
          statusText = status;
          statusColor = 'info';
      }

      return {
        name: 'Build Status',
        value: statusText,
        status: statusColor as 'healthy' | 'warning' | 'error' | 'info',
        description: `🔄 Real data from GitLab CI: ${status} (${duration ? `${duration}s` : 'unknown duration'}) - ${createdAt.toLocaleDateString()}`,
      };

    } catch (error) {
      console.error('Error fetching build status:', error);
      return {
        name: 'Build Status',
        value: 'Error',
        status: 'error',
        description: `❌ Failed to fetch build status: ${error.message}`,
      };
    }
  }

  async getNpmAuditStatus(
    projectId: string, 
    securityReportsConfig?: string
  ): Promise<HealthMetric> {
    try {
      if (!this.gitlabToken) {
        return {
          name: 'Dependencies Vulnerability',
          value: 'No Token',
          status: 'error',
          description: '❌ GitLab API token missing.',
        };
      }

      // Get project info to construct Pages URL
      const projectResponse = await fetch(
        `https://git.agilie.com/api/v4/projects/${projectId}`,
        {
          headers: {
            'PRIVATE-TOKEN': this.gitlabToken,
          },
        }
      );

      if (!projectResponse.ok) {
        throw new Error(`GitLab API error: ${projectResponse.status}`);
      }

      const project = await projectResponse.json();
      const projectPath = project.path_with_namespace;
      
      // Check for pages_reports job
      const jobsResponse = await fetch(
        `https://git.agilie.com/api/v4/projects/${projectId}/jobs?per_page=20&scope[]=success`,
        {
          headers: {
            'PRIVATE-TOKEN': this.gitlabToken,
          },
        }
      );

      if (!jobsResponse.ok) {
        throw new Error(`GitLab API error: ${jobsResponse.status}`);
      }

      const jobs = await jobsResponse.json();
      
      // Look for pages_reports job
      const pagesReportsJob = jobs.find((job: any) => 
        job.name?.toLowerCase().includes('pages_reports') || 
        job.name?.toLowerCase().includes('pages-reports')
      );

      if (pagesReportsJob && pagesReportsJob.status === 'success') {
        // Construct Pages URLs for audit reports
        const pagesBaseUrl = `http://pages.scrij.com/${projectPath}`;
        
        // Parse security reports configuration from annotation - REQUIRED
        if (!securityReportsConfig) {
          return {
            name: 'Dependencies Vulnerability',
            value: 'Not Configured',
            status: 'warning',
            description: '⚠️ Security reports are not configured. Add backstage.io/security-reports annotation to catalog-info.yaml with report configuration.',
          };
        }
        
        let reports: Array<{ name: string; url: string }> = [];
        
        try {
          const config = JSON.parse(securityReportsConfig);
          if (Array.isArray(config)) {
            // If config is array of report objects
            reports = config.map((report: { name: string; file: string }) => ({
              name: report.name,
              url: `${pagesBaseUrl}/${report.file}`,
            }));
          } else if (typeof config === 'object' && config.reports) {
            // If config has reports property
            reports = config.reports.map((report: { name: string; file: string }) => ({
              name: report.name,
              url: `${pagesBaseUrl}/${report.file}`,
            }));
          }
        } catch (parseError) {
          // eslint-disable-next-line no-console
          console.warn('Failed to parse security reports config:', parseError);
          return {
            name: 'Dependencies Vulnerability',
            value: 'Configuration Error',
            status: 'warning',
            description: '⚠️ Failed to parse security reports configuration. Check backstage.io/security-reports annotation format in catalog-info.yaml.',
          };
        }
        
        // If no reports found after parsing
        if (reports.length === 0) {
          return {
            name: 'Dependencies Vulnerability',
            value: 'Not Configured',
            status: 'warning',
            description: '⚠️ Security reports configuration is empty. Add report entries to backstage.io/security-reports annotation in catalog-info.yaml.',
          };
        }

        const lastRun = new Date(pagesReportsJob.created_at).toLocaleDateString();
        
        return {
          name: 'Dependencies Vulnerability',
          value: '✅ Configured',
          status: 'healthy',
          description: `📊 ${reports.length} reports configured. Last run: ${lastRun}.`,
          links: reports.map(r => ({ url: r.url, title: r.name })),
          reportUrls: reports.map(r => ({ url: r.url, title: r.name })), // All reports for iframe display
        };
      }

      // If pages_reports job not found or not successful, return warning
      return {
        name: 'Dependencies Vulnerability',
        value: 'Not Configured',
        status: 'warning',
        description: '⚠️ Security reports are not configured. Configure pages_reports job in GitLab CI/CD to generate audit reports.',
      };

    } catch (error: any) {
      console.error('Error fetching dependencies vulnerability status:', error);
      return {
        name: 'Dependencies Vulnerability',
        value: 'Error',
        status: 'error',
        description: `❌ Failed to fetch dependencies vulnerability data: ${error.message}`,
      };
    }
  }

  async getTrivyScanStatus(
    projectId: string,
    trivyConfig?: string
  ): Promise<HealthMetric> {
    try {
      if (!this.gitlabToken) {
        return {
          name: 'Environment Scan (Trivy)',
          value: 'No Token',
          status: 'error',
          description: '❌ GitLab API token missing',
        };
      }

      // Look for Trivy/security scan jobs in GitLab CI/CD
      // Search for jobs with names containing "security_test", "trivy", "container-scan", or "image-scan"
      const response = await fetch(
        `https://git.agilie.com/api/v4/projects/${projectId}/jobs?per_page=50`,
        {
          headers: {
            'PRIVATE-TOKEN': this.gitlabToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`GitLab API error: ${response.status}`);
      }

      const jobs = await response.json();
      
      // Parse Trivy job configuration from annotation - REQUIRED
      if (!trivyConfig) {
        return {
          name: 'Environment Scan (Trivy)',
          value: 'Not Configured',
          status: 'warning',
          description: '⚠️ Trivy scan configuration is missing. Add backstage.io/trivy-config annotation to catalog-info.yaml with job patterns.',
        };
      }
      
      let jobPatterns: string[] = [];
      
      try {
        const config = JSON.parse(trivyConfig);
        if (Array.isArray(config)) {
          jobPatterns = config;
        } else if (typeof config === 'object' && config.patterns) {
          jobPatterns = config.patterns;
        } else if (typeof config === 'string') {
          jobPatterns = [config];
        }
      } catch (parseError) {
        // eslint-disable-next-line no-console
        console.warn('Failed to parse Trivy config:', parseError);
        return {
          name: 'Environment Scan (Trivy)',
          value: 'Configuration Error',
          status: 'warning',
          description: '⚠️ Failed to parse Trivy configuration. Check backstage.io/trivy-config annotation format in catalog-info.yaml.',
        };
      }
      
      // If no patterns found after parsing
      if (jobPatterns.length === 0) {
        return {
          name: 'Environment Scan (Trivy)',
          value: 'Not Configured',
          status: 'warning',
          description: '⚠️ Trivy configuration is empty. Add job patterns to backstage.io/trivy-config annotation in catalog-info.yaml.',
        };
      }
      
      // Find security test jobs based on configured patterns
      const securityJobs = jobs.filter((job: any) => {
        const jobName = job.name?.toLowerCase() || '';
        const jobStage = job.stage?.toLowerCase() || '';
        
        return jobPatterns.some(pattern => 
          jobName.includes(pattern.toLowerCase()) ||
          jobStage.includes(pattern.toLowerCase())
        );
      });

      if (securityJobs.length === 0) {
        return {
          name: 'Environment Scan (Trivy)',
          value: 'Not Found',
          status: 'warning',
          description: `⚠️ No jobs found matching configured patterns: ${jobPatterns.join(', ')}. Check job names in GitLab CI/CD pipeline.`,
        };
      }

      // Get the most recent security test job
      const latestJob = securityJobs.sort((a: any, b: any) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];

      const status = latestJob.status;
      const jobName = latestJob.name;
      const createdAt = new Date(latestJob.created_at);
      const finishedAt = latestJob.finished_at ? new Date(latestJob.finished_at) : null;

      let statusText = '';
      let statusColor: 'healthy' | 'warning' | 'error' | 'info' = 'info';

      switch (status) {
        case 'success':
          statusText = '✅ Scan Passed';
          statusColor = 'healthy';
          break;
        case 'failed':
          statusText = '❌ Scan Failed';
          statusColor = 'error';
          break;
        case 'running':
          statusText = '🔄 Scanning...';
          statusColor = 'warning';
          break;
        case 'pending':
          statusText = '⏳ Pending';
          statusColor = 'warning';
          break;
        case 'canceled':
          statusText = '🚫 Canceled';
          statusColor = 'info';
          break;
        default:
          statusText = status;
          statusColor = 'info';
      }

      const timeAgo = finishedAt 
        ? `${Math.floor((Date.now() - finishedAt.getTime()) / (1000 * 60 * 60))}h ago`
        : createdAt.toLocaleDateString();

      return {
        name: 'Environment Scan (Trivy)',
        value: statusText,
        status: statusColor,
        description: `🔄 Real data from GitLab CI: ${jobName} - ${status} (${timeAgo}). Check GitLab for detailed vulnerability report.`,
      };
    } catch (error: any) {
      console.error('Error fetching Trivy scan status:', error);
      return {
        name: 'Environment Scan (Trivy)',
        value: 'Error',
        status: 'error',
        description: `❌ Failed to fetch Trivy scan data: ${error.message || 'Unknown error'}`,
      };
    }
  }
}
